import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import styled from 'styled-components/macro'
import NutsModal from 'components/modals/NutsModal'
import { getRegionsAsList, updateSelectedFilters } from 'store/actions/filters'
import { NUTS_LEVEL_COLOR_MAPPING, SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import Dotdotdot from 'react-dotdotdot'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'
import latinize from 'latinize'

const NutsSelectedFilter = styled(SelectedFilter)`
  color: white;
  background-color: ${props => NUTS_LEVEL_COLOR_MAPPING[props.level]};
  div {
    color: white !important;
  }
  i:before {
    color: white !important;
  }
`

const MapButton = styled(Button)`
  font-size: 12px;
  width: auto;
`
const MarkerIcon = styled.i`
  margin-left: 4px;
`
const SpinnerContainer = styled.div`
  min-height: 20px;
  position: relative;
`

function NutsSelector({
  type,
  values,
  filterData,
  title,
  showFilter,
  customTop,
  selection,
  sidebarReduced,
  modalReset,
  triggerButton
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable, regions } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]
  return (
    <PureNutsSelector
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      showFilter={showFilter}
      customTop={customTop}
      filterObject={filterObject}
      regions={regions}
      selection={selection}
      sidebarReduced={sidebarReduced}
      modalReset={modalReset}
      triggerButton={triggerButton}
    />
  )
}

const handleNutsName = nuts =>
  nuts.english_alias !== null && nuts.english_alias !== ''
    ? `${latinize(nuts.english_alias)} / ${nuts.name}`
    : nuts.name

const getOptions = (regions, selectedOptions) =>
  regions.map(option => {
    const parentOption = {
      label: `[${option.nuts_id}] ${handleNutsName(option)}`,
      value: option.nuts_id,
      type: option.type
    }
    const selectedOption = selectedOptions.filter(s => s.id === option.nuts_id)
    if (selectedOption.length > 0) {
      parentOption.checked = true
    }
    return parentOption
  })

const PureNutsSelector = React.memo(function PureNutsSelector({
  type,
  values,
  filterData,
  title,
  showFilter,
  customTop,
  filterObject,
  regions,
  selection,
  sidebarReduced,
  modalReset,
  triggerButton
}) {
  const dispatch = useDispatch()
  const selectedValues = filterObject[values[0]]
  const options = getOptions(regions || [], selectedValues)

  const [nutsPartnerModalOpen, setNutsPartnerModalOpen] = useState(false)

  useEffect(() => {
    setNutsPartnerModalOpen(modalReset ? nutsPartnerModalOpen : false)
  })

  const onChange = (currentNode, selectedNodes) => {
    const filterValue = selectedNodes.map(node => ({
      id: node.value,
      label: node.label,
      paths: node.paths,
      type: node.type
    }))

    const data = {
      filterType: type,
      filterId: values[0],
      filterValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function setNutsFilter(nuts) {
    const data = {
      filterType: type,
      filterId: values[0],
      filterValue: nuts
    }

    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
    let resultingValue = null
    if (valueIsArray) {
      resultingValue = filterObject[filterId].filter(
        s => s.id !== filterValue.id || s !== filterValue
      )
    }
    const data = {
      filterType: type,
      filterId,
      filterValue: resultingValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function updateSelectedNuts(fetchedNuts) {
    const filterValue = fetchedNuts.map(nut => ({
      id: nut.nuts_id,
      label: nut.name,
      paths: nut.paths,
      type: nut.type
    }))

    const data = {
      filterType: type,
      filterId: values[0],
      filterValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function toggleNutsModal(reset) {
    triggerButton(true)
    if (reset) {
      setNutsPartnerModalOpen(false)
    } else {
      setNutsPartnerModalOpen(prevState => !prevState)
    }
  }

  function fetchSelectedNuts() {
    let nutCounter = 0
    const fetchedNuts = []
    const noPaths = selectedValues.filter(value => !value.paths)
    toggleNutsModal()
    if (noPaths.length > 0) {
      selectedValues.forEach(nut => {
        if (!nut.paths) {
          const params = {
            name: nut.id
          }
          dispatch(getRegionsAsList(params)).then(response => {
            nutCounter += 1
            fetchedNuts.push(response.value[0])
            if (nutCounter === selectedValues.length) {
              updateSelectedNuts(fetchedNuts)
            }
          })
        }
      })
    }
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject[value]) {
        const filterValue = filterObject[value]
        if (filterValue instanceof Array) {
          return filterValue.map(v => (
            <SelectedFilterContainer className="selected-filters" key={v.id}>
              <NutsSelectedFilter level={v.type}>
                <FilterText title={v.label}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label}</Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleFilterRemove(value, v, true)}
                />
              </NutsSelectedFilter>
            </SelectedFilterContainer>
          ))
        }
      }
      return false
    })
  }

  return (
    <>
      {!selection ? (
        <SearchFilter open={showFilter} customTop={customTop}>
          <div className="sticky-container">
            {title ? <FilterTitle>{title}</FilterTitle> : null}
            {!filterData && !regions ? (
              <SpinnerContainer>
                <LoadingSpinner showText={false} />
              </SpinnerContainer>
            ) : (
              <>
                <CustomDropDownTreeSelect data={options} onChange={onChange} hideDropDown />
                <MapButton size="small" onClick={fetchSelectedNuts}>
                  {t`Choose on map`} <MarkerIcon className="fas fa-map-marker-alt" />
                </MapButton>
              </>
            )}
          </div>
          {!selection && nutsPartnerModalOpen && (
            <NutsModal
              modalOpen={modalReset}
              toggleModal={reset => toggleNutsModal(reset)}
              setNutsFilter={setNutsFilter}
              selectedValues={selectedValues}
              disableNutsSelect={false}
              sidebarReduced={sidebarReduced}
              defaultClosed={showFilter}
            />
          )}
        </SearchFilter>
      ) : (
        <div className="selected-fl">{renderSelectedValues()}</div>
      )}
    </>
  )
})

NutsSelector.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  selection: PropTypes.bool,
  sidebarReduced: PropTypes.bool,
  modalReset: PropTypes.bool,
  triggerButton: PropTypes.func
}

PureNutsSelector.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  regions: PropTypes.array,
  selection: PropTypes.bool,
  sidebarReduced: PropTypes.bool,
  modalReset: PropTypes.bool,
  triggerButton: PropTypes.func
}

export default NutsSelector
